import { useEvent } from "shared/lib/useEvent"
import { ProductConfigurationFormData } from "shared/types/ProductConfiguration"
import { ProductConfigurationResponse } from "shared/types/__generated__/graphql"

import { useProductConfigurationByFormDataLazy } from "entities/dim-configuration/api/useProductConfigurationByFormData"
import {
  useProductConfigurationQueryWithCache,
  useProductConfigurationQueryWithCacheLazy
} from "entities/dim-configuration/api/useProductConfigurationQueryWithCache"
import {
  ProductConfigurationQuery,
  useProductConfigurationLazyQuery,
  useProductConfigurationModelsQuery
} from "entities/dim-configuration/graphql/product-configuration.queries.generated"
import { getProductConfigurationQueryVariables } from "entities/dim-configuration/lib/getProductConfigurationQueryVariables"
import { combineVariantKeys } from "entities/products/lib/combineVariantKeys"
import { getProductType } from "entities/products/lib/getProductType"
import { ProductConfiguratorResult } from "widgets/product-configurator/model/useProductConfiguratorModel"

import { SchemaProcessor } from "./useProductConfigurationSchemaProcessor"
import { useVariantKey } from "./useVariantKey"

interface UseProductConfigurationAPIProps {
  variantKey: string
  configurationKey: string
  externalDimensioningID: string
  onConfigurationChange?: (configuration: ProductConfiguratorResult) => void
  onCompleted?: (response: ProductConfigurationResponse) => void
  onError?: () => void
  processSchema: SchemaProcessor
}

export function useProductConfigurationAPI({
  variantKey,
  configurationKey,
  externalDimensioningID,
  onCompleted,
  onError,
  onConfigurationChange
}: UseProductConfigurationAPIProps) {
  const { productType, attributes } = useVariantKey(variantKey)
  const { data: models, loading: isLoadingModels } =
    useProductConfigurationModelsQuery()
  const [refetchByVariantKey, { loading: isLoadingByVariantKeyLazy }] =
    useProductConfigurationQueryWithCacheLazy()

  const hasModel = models?.configurationModels?.models?.some(
    model => model.id === productType
  )

  const [fetchByFormData, { loading: isLoadingFormDataQuery }] =
    useProductConfigurationByFormDataLazy()

  const handleResponse = useEvent(
    (response: ProductConfigurationResponse | null | undefined) => {
      if (response) {
        onCompleted?.(response)
        onConfigurationChange?.({
          variantKey: response.variantKey || productType,
          configurationKey: response.configurationKey,
          externalDimensioningResult: response.dimensioningKey
            ? undefined
            : null
        })
      }
    }
  )

  const handleFetchByFormData = useEvent(
    (formData: ProductConfigurationFormData, fieldID?: string) => {
      return fetchByFormData({
        variables: {
          productType: getProductType(variantKey),
          formData,
          lastModifiedAttribute: fieldID,
          dimensioningKey: externalDimensioningID
        },
        onError
      }).then(response => handleResponse(response.data?.productConfiguration))
    }
  )

  const handleProductTypeQueryCompleted = useEvent(
    (data: ProductConfigurationQuery) => {
      if (attributes.length > 0 && data.productConfiguration) {
        refetchByVariantKey({
          variables: getProductConfigurationQueryVariables({
            productType,
            variantKey: combineVariantKeys(
              data.productConfiguration.variantKey,
              variantKey
            )
          })
        }).then(result => handleResponse(result.data?.productConfiguration))
      } else {
        handleResponse(data.productConfiguration)
      }
    }
  )

  const fetchByVariantKey = useEvent((nextVariantKey: string) => {
    refetchByVariantKey({
      variables: getProductConfigurationQueryVariables({
        productType,
        variantKey: nextVariantKey
      })
    }).then(result => handleResponse(result.data?.productConfiguration))
  })

  const {
    data: productConfigurationByProductTypeResponseData,
    error: productTypeError,
    loading: isLoadingProductTypeQuery,
    refetch: refetchByProductType
  } = useProductConfigurationQueryWithCache({
    variables: getProductConfigurationQueryVariables({ productType }),
    skip: !!configurationKey || !!externalDimensioningID || !hasModel,
    onCompleted: handleProductTypeQueryCompleted,
    onError
  })

  const handleVariantKeyQueryCompleted = useEvent(
    (data: ProductConfigurationQuery) => {
      handleResponse(data.productConfiguration)
    }
  )

  const { loading: isLoadingVariantKeyQuery } =
    useProductConfigurationQueryWithCache({
      variables: getProductConfigurationQueryVariables({
        productType,
        variantKey
      }),
      skip:
        !!configurationKey ||
        !!externalDimensioningID ||
        !hasModel ||
        variantKey === productType,
      onCompleted: handleVariantKeyQueryCompleted
    })

  const handleConfigurationKeyQueryCompleted = useEvent(
    (data: ProductConfigurationQuery) => {
      handleResponse(data.productConfiguration)
    }
  )

  const handleConfigurationKeyQueryError = useEvent(() => {
    onError?.()
    refetchByProductType({ productType })
  })

  const {
    data: productConfigurationResponseData,
    previousData: previousProductConfigurationResponseData,
    loading: isLoadingConfigurationKeyQuery
  } = useProductConfigurationQueryWithCache({
    variables: getProductConfigurationQueryVariables({
      productType,
      configurationKey,
      dimensioningKey: externalDimensioningID
    }),
    skip: (!configurationKey && !externalDimensioningID) || !hasModel,
    onCompleted: handleConfigurationKeyQueryCompleted,
    onError: handleConfigurationKeyQueryError
  })

  const [
    fetchByConfigurationKeyLazy,
    { loading: isLoadingConfigurationKeyQueryLazy }
  ] = useProductConfigurationLazyQuery({
    variables: getProductConfigurationQueryVariables({
      productType,
      configurationKey,
      dimensioningKey: externalDimensioningID
    }),
    onError: handleConfigurationKeyQueryError
  })

  const isConfigurationModelFaulty =
    hasModel &&
    (!!productTypeError ||
      !!productConfigurationByProductTypeResponseData?.productConfiguration
        ?.error)
  const isConfigurable = hasModel && !isConfigurationModelFaulty

  const fetchByConfigurationKey = useEvent((nextConfigurationKey: string) => {
    fetchByConfigurationKeyLazy({
      variables: getProductConfigurationQueryVariables({
        productType,
        configurationKey: nextConfigurationKey,
        dimensioningKey: externalDimensioningID
      })
    })
  })

  return {
    isConfigurable,
    isConfigurationModelFaulty,
    productConfigurationResponse:
      productConfigurationResponseData?.productConfiguration ||
      previousProductConfigurationResponseData?.productConfiguration,
    fetchByVariantKey,
    fetchByConfigurationKey,
    fetchByFormData: handleFetchByFormData,
    isLoading:
      isLoadingModels ||
      isLoadingProductTypeQuery ||
      isLoadingVariantKeyQuery ||
      isLoadingConfigurationKeyQuery ||
      isLoadingFormDataQuery ||
      isLoadingByVariantKeyLazy ||
      isLoadingConfigurationKeyQueryLazy,
    isLoadingModels
  }
}
